import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { formatCurrency, formatDate } from '../utils'; 
import { buscarJurosAutomaticosBackend } from '../utils/apiJuros';
import { addDays } from 'date-fns';

/**
 * Componente de modal para realizar pagamentos
 */
const ModalPagamento = ({
  open,
  onClose,
  emprestimo,
  parcelasSelecionadas,
  jurosPercentual,
  setJurosPercentual,
  descontoPercentual,
  setDescontoPercentual,
  valoresPagamento,
  setValoresPagamento,
  novasDatasVencimento,
  setNovasDatasVencimento,
  observacoes,
  setObservacoes,
  handlePagamento,
  handleParcelaSelection,
  aplicarJurosAutomaticos = false,
  setAplicarJurosAutomaticos,
  emprestimos,
  selecionarTodasParcelas,
  removerTodasSelecoes,
  valoresCalculados,
  setValoresCalculados,
  tipoJuros,
  setTipoJuros,
  tipoDesconto,
  setTipoDesconto,
  valorJurosFixo,
  setValorJurosFixo,
  valorDescontoFixo,
  setValorDescontoFixo,
  handleValorPagamentoChange,
  handleValorPagoChange,
  handleJurosAutomaticosChange
}) => {
  const [valoresPagos, setValoresPagos] = useState({});
  const [valoresOriginais, setValoresOriginais] = useState({});
  const [detalhesCalculos] = useState({});
  const [emProcessamento] = useState(false);
  const [jurosAutomaticos, setJurosAutomaticos] = useState({});
  
  // Novo estado para a checkbox
  const [aplicarJurosEAtualizar, setAplicarJurosEAtualizar] = useState(false);

  // Novo estado para checkboxes individuais de "pagar apenas juros"
  const [pagarApenasJuros, setPagarApenasJuros] = useState({});

  // Dependência baseada em string única dos IDs e status das parcelas selecionadas
  const parcelasSelecionadasKey = parcelasSelecionadas
    ? parcelasSelecionadas.map(p => `${p.id}:${p.selected ? 1 : 0}:${p.status}`).join(',')
    : '';

  // Inicializa os valores quando o modal é aberto e as parcelas mudam
  useEffect(() => {
    if (open && parcelasSelecionadas && parcelasSelecionadas.length > 0) {
      const novosValoresOriginais = {};
      const novosValoresPagamento = {};
      const novosValoresPagos = {};
      
      parcelasSelecionadas.forEach(parcela => {
        novosValoresOriginais[parcela.id] = parcela.valor;
        novosValoresPagamento[parcela.id] = parcela.valor;
        novosValoresPagos[parcela.id] = parcela.valor;
      });
      
      setValoresOriginais(novosValoresOriginais);
      setValoresPagamento(novosValoresPagamento);
      setValoresPagos(novosValoresPagos);
    }
  }, [open, parcelasSelecionadas, setValoresPagamento]);

  // Atualiza valores quando juros manuais mudam
  useEffect(() => {
    if (parcelasSelecionadas && parcelasSelecionadas.length > 0) {
      const jurosManuais = parseFloat(valorJurosFixo || 0);
      
      if (jurosManuais > 0) {
        const novosValoresPagamento = { ...valoresPagamento };
        const novosValoresPagos = { ...valoresPagos };
        
        parcelasSelecionadas.forEach(parcela => {
          if (parcela.selected) {
            const valorOriginal = valoresOriginais[parcela.id] || 0;
            const valorComJuros = valorOriginal + jurosManuais;
            
            novosValoresPagamento[parcela.id] = valorComJuros;
            novosValoresPagos[parcela.id] = valorComJuros;
          }
        });
        
        setValoresPagamento(novosValoresPagamento);
        setValoresPagos(novosValoresPagos);
      }
    }
  }, [valorJurosFixo, parcelasSelecionadas, valoresOriginais, valoresPagamento, setValoresPagamento]);

  useEffect(() => {
    console.log('Chamando backend para juros automáticos', parcelasSelecionadas, aplicarJurosAutomaticos);
    async function carregarJuros() {
      if (parcelasSelecionadas && parcelasSelecionadas.length > 0) {
        const novosJuros = {};
        for (const parcela of parcelasSelecionadas) {
          if (parcela.status === 'atrasado' && parcela.selected && aplicarJurosAutomaticos) {
            try {
              const dados = await buscarJurosAutomaticosBackend(parcela.id);
              novosJuros[parcela.id] = dados;
            } catch (e) {
              novosJuros[parcela.id] = { valor: 0, percentual: 0 };
            }
          } else {
            novosJuros[parcela.id] = { valor: 0, percentual: 0 };
          }
        }
        setJurosAutomaticos(novosJuros);
      }
    }
    carregarJuros();
  }, [parcelasSelecionadasKey, aplicarJurosAutomaticos, parcelasSelecionadas]);

  // Função para calcular os totais
  const calcularTotais = useCallback(() => {
    if (!parcelasSelecionadas || parcelasSelecionadas.length === 0) {
      return {
        totalOriginal: 0,
        totalAPagar: 0,
        totalPago: 0,
        totalPagoSelecionado: 0,
        totalSaldo: 0,
        totalJuros: 0,
        totalDesconto: 0
      };
    }

    let totalOriginal = 0;
    let totalAPagar = 0;
    let totalPago = 0;
    let totalPagoSelecionado = 0;
    let totalJuros = 0;
    let totalDesconto = 0;

    // Calcular juros manuais totais
    const jurosManuaisTotal = parseFloat(valorJurosFixo || 0);

    parcelasSelecionadas.forEach(parcela => {
      const valorOriginal = valoresOriginais[parcela.id] || 0;
      let valorAPagar = valoresPagamento[parcela.id] || 0;
      const valorPago = valoresPagos[parcela.id] || 0;
      
      // Se a parcela está selecionada, adicionar juros manuais ao valor a pagar
      if (parcela.selected && jurosManuaisTotal > 0) {
        valorAPagar += jurosManuaisTotal;
      }
      
      totalOriginal += valorOriginal;
      totalAPagar += valorAPagar;
      
      if (parcela.selected) {
        totalPagoSelecionado += valorPago;
      }
      
      // Calcular juros e descontos
      if (valorAPagar > valorOriginal) {
        totalJuros += (valorAPagar - valorOriginal);
      } else if (valorAPagar < valorOriginal) {
        totalDesconto += (valorOriginal - valorAPagar);
      }
    });

    return {
      totalOriginal,
      totalAPagar,
      totalPago,
      totalPagoSelecionado,
      totalSaldo: totalAPagar - totalPagoSelecionado,
      totalJuros,
      totalDesconto
    };
  }, [parcelasSelecionadas, valoresOriginais, valoresPagamento, valoresPagos, valorJurosFixo]);
  

  


  // Callback para lidar com mudanças na data de vencimento
  const handleNovaDataVencimentoChange = useCallback((parcelaId, novaData) => {
    setNovasDatasVencimento(prevState => ({
      ...prevState,
      [parcelaId]: novaData
    }));
  }, [setNovasDatasVencimento]);

  // Função para confirmar pagamento
  const handleConfirmarPagamento = useCallback(() => {
    if (handlePagamento) {
      // Preparar os dados para envio
      const pagamentos = parcelasSelecionadas
        .filter(parcela => parcela.selected)
        .map(parcela => ({
          parcela_id: parcela.id,
          valor_pago: valoresPagos[parcela.id] || 0,
          valor_original: valoresOriginais[parcela.id] || 0,
          valor_com_juros: valoresPagamento[parcela.id] || 0,
          juros_valor: detalhesCalculos[parcela.id]?.jurosManual?.valor || 0,
          juros_percentual: detalhesCalculos[parcela.id]?.jurosManual?.percentual || 0,
          juros_automaticos_valor: detalhesCalculos[parcela.id]?.jurosAutomaticos?.valor || 0,
          juros_automaticos_percentual: detalhesCalculos[parcela.id]?.jurosAutomaticos?.percentual || 0,
          desconto_valor: detalhesCalculos[parcela.id]?.desconto?.valor || 0,
          desconto_percentual: detalhesCalculos[parcela.id]?.desconto?.percentual || 0,
          nova_data_vencimento: novasDatasVencimento[parcela.id] || null,
          observacoes: observacoes || ''
        }));

      handlePagamento(pagamentos);
    }
  }, [handlePagamento, parcelasSelecionadas, valoresPagos, valoresOriginais, valoresPagamento, detalhesCalculos, novasDatasVencimento, observacoes]);

  // Função para aplicar pagamento apenas dos juros e renovar parcela
  useEffect(() => {
    function aplicarPagamentoApenasJuros() {
      if (aplicarJurosEAtualizar && parcelasSelecionadas && parcelasSelecionadas.length > 0 && emprestimo) {
        const novosValoresPagamento = { ...valoresPagamento };
        const novosValoresPagos = { ...valoresPagos };
        const novasDatas = { ...novasDatasVencimento };
        const taxaJuros = parseFloat(emprestimo.taxa_juros) || 0;
        for (const parcela of parcelasSelecionadas) {
          if (parcela.selected) {
            // SEMPRE usar o valor_juros já calculado e armazenado no banco
            const valorJuros = parcela.valor_juros !== undefined && parcela.valor_juros !== null
              ? parseFloat(parcela.valor_juros)
              : 0;
            // Preenche juros manuais (se existir campo)
            if (setJurosPercentual) setJurosPercentual(taxaJuros);
            if (setValorJurosFixo) setValorJurosFixo(valorJuros);
            // Preenche valor pago e valor a pagar (apenas juros)
            novosValoresPagamento[parcela.id] = valorJuros;
            novosValoresPagos[parcela.id] = valorJuros;
            // Atualiza data de vencimento para 30 dias após o vencimento original
            const dataVencStr = parcela.data_vencimento ? String(parcela.data_vencimento).split('T')[0] : '';
            const dataBase = dataVencStr ? new Date(`${dataVencStr}T12:00:00`) : new Date();
            const novaData = addDays(dataBase, 30);
            novasDatas[parcela.id] = novaData.toISOString().slice(0, 10);
          }
        }
        setValoresPagamento(novosValoresPagamento);
        setValoresPagos(novosValoresPagos);
        setNovasDatasVencimento(novasDatas);
      }
    }
    aplicarPagamentoApenasJuros();
    // eslint-disable-next-line
  }, [aplicarJurosEAtualizar]);

  // Função para aplicar pagamento apenas dos juros para uma parcela
  const handlePagarApenasJuros = (parcela) => {
    const novosPagarApenasJuros = { ...pagarApenasJuros, [parcela.id]: !pagarApenasJuros[parcela.id] };
    setPagarApenasJuros(novosPagarApenasJuros);

    if (!pagarApenasJuros[parcela.id]) { // Se está marcando agora
      const taxaJuros = parseFloat(emprestimo.taxa_juros) || 0;
      const valorOriginal = parseFloat(parcela.valor_original || parcela.valor || 0);
      const valorJuros = parcela.valor_juros !== undefined && parcela.valor_juros !== null
        ? parseFloat(parcela.valor_juros)
        : (taxaJuros > 0 ? valorOriginal * (taxaJuros / 100) : 0);
      // Preenche juros manuais (se existir campo)
      if (setJurosPercentual) setJurosPercentual(taxaJuros);
      if (setValorJurosFixo) setValorJurosFixo(valorJuros);
      // Preenche valor pago e valor a pagar (apenas juros)
      setValoresPagamento(prev => ({ ...prev, [parcela.id]: valorJuros }));
      setValoresPagos(prev => ({ ...prev, [parcela.id]: valorJuros }));
      // Atualiza data de vencimento para 30 dias após o vencimento original
      const dataVencStr = parcela.data_vencimento ? String(parcela.data_vencimento).split('T')[0] : '';
      const dataBase = dataVencStr ? new Date(`${dataVencStr}T12:00:00`) : new Date();
      const novaData = addDays(dataBase, 30);
      setNovasDatasVencimento(prev => ({ ...prev, [parcela.id]: novaData.toISOString().slice(0, 10) }));
    }
  };

  // Return do componente com JSX corrigido
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        style: {
          minHeight: '80vh',
          maxHeight: '95vh',
          width: '1100px',
          overflow: 'auto',
          padding: 0,
        },
        sx: {
          '@media (max-width: 600px)': {
            minWidth: '100vw',
            maxWidth: '100vw',
            width: '100vw',
            borderRadius: 0,
            m: 0,
            p: 0,
          }
        }
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, p: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, color: '#1976d2' }}>
            Realizar Pagamento - {emprestimo?.nome_cliente || emprestimo?.nome || emprestimo?.id || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'flex-start', sm: 'center' }, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Parcelas Pendentes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={selecionarTodasParcelas}
                fullWidth={true}
                sx={{ minWidth: 0, fontSize: { xs: '0.9rem', sm: '1rem' }, py: 0.5 }}
              >
                SELECIONAR TODAS
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={removerTodasSelecoes}
                fullWidth={true}
                sx={{ minWidth: 0, fontSize: { xs: '0.9rem', sm: '1rem' }, py: 0.5 }}
              >
                REMOVER SELEÇÃO
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
        <Box>
          {parcelasSelecionadas && parcelasSelecionadas.length > 0 && (
            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: { xs: 1, sm: 2 }, backgroundColor: '#eee' }}>
              <Typography variant="subtitle1" gutterBottom>Resumo das Parcelas Selecionadas</Typography>
              {/* MOBILE: Lista de cartões */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {parcelasSelecionadas.map((parcela) => (
                  <Box key={parcela.id} sx={{ mb: 2, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Checkbox
                        checked={!!parcela.selected}
                        onChange={() => handleParcelaSelection(parcela.id)}
                      />
                      <Typography variant="body2" fontWeight="bold" ml={1}>
                        Parcela {parcela.numero_parcela}
                      </Typography>
                      <Box flex={1} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(parcela.data_vencimento)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">Valor Original: {formatCurrency(parcela.valor)}</Typography>
                    <Typography variant="body2" color="primary">
                      Juros Automáticos: {formatCurrency(jurosAutomaticos[parcela.id]?.valor ?? 0)}
                    </Typography>
                    {jurosAutomaticos[parcela.id]?.valor > 0 && (
                      <Box fontSize="0.95em" color="#555">
                        {jurosAutomaticos[parcela.id]?.diasAtraso !== undefined && (
                          <span>Dias atraso: {jurosAutomaticos[parcela.id].diasAtraso} <br /></span>
                        )}
                        {jurosAutomaticos[parcela.id]?.percentual !== undefined && (
                          <span>Percentual: {jurosAutomaticos[parcela.id].percentual}%</span>
                        )}
                      </Box>
                    )}
                    <TextField
                      label="Valor a Pagar"
                      type="number"
                      size="small"
                      value={valoresPagamento[parcela.id] || parcela.valor}
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        readOnly: true,
                      }}
                    />
                    <TextField
                      label="Valor Pago"
                      type="number"
                      size="small"
                      value={valoresPagos[parcela.id] || ''}
                      onChange={e => handleValorPagoChange(parcela.id, e.target.value)}
                      fullWidth
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    />
                    <TextField
                      label="Nova Data Vencimento"
                      type="date"
                      size="small"
                      value={novasDatasVencimento[parcela.id] || ''}
                      onChange={e => handleNovaDataVencimentoChange(parcela.id, e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!pagarApenasJuros[parcela.id]}
                          onChange={() => handlePagarApenasJuros(parcela)}
                          color="primary"
                        />
                      }
                      label="Pagar apenas juros"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ))}
              </Box>
              {/* DESKTOP: Tabela */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Selecionar</TableCell>
                      <TableCell>Parcela</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell>Valor Original</TableCell>
                      <TableCell>Juros Automáticos</TableCell>
                      <TableCell>Valor a Pagar</TableCell>
                      <TableCell>Valor Pago</TableCell>
                      <TableCell>Nova Data Vencimento</TableCell>
                      <TableCell>Pagar apenas juros</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parcelasSelecionadas.map((parcela) => (
                      <TableRow key={parcela.id}>
                        <TableCell>
                          <Checkbox
                            checked={!!parcela.selected}
                            onChange={() => handleParcelaSelection(parcela.id)}
                          />
                        </TableCell>
                        <TableCell>{parcela.numero_parcela}</TableCell>
                        <TableCell>{formatDate(parcela.data_vencimento)}</TableCell>
                        <TableCell>{formatCurrency(parcela.valor)}</TableCell>
                        <TableCell style={{ minHeight: 48, whiteSpace: 'normal' }}>
                          <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                            {formatCurrency(jurosAutomaticos[parcela.id]?.valor ?? 0)}
                          </span>
                          {jurosAutomaticos[parcela.id]?.taxa !== undefined && (
                            <div style={{ fontSize: '0.85em', color: '#555', marginTop: 2 }}>
                              Taxa: {formatCurrency(parseFloat(jurosAutomaticos[parcela.id].taxa))}
                              {jurosAutomaticos[parcela.id].periodicidade === 'diario' && ' ao dia'}
                              {jurosAutomaticos[parcela.id].periodicidade === 'mensal' && ' ao mês'}
                              {jurosAutomaticos[parcela.id].periodicidade === 'semanal' && ' à semana'}
                            </div>
                          )}
                          {jurosAutomaticos[parcela.id]?.valor > 0 && (
                            <div style={{ fontSize: '0.85em', color: '#555' }}>
                              {jurosAutomaticos[parcela.id]?.diasAtraso !== undefined && (
                                <span>Dias atraso: {jurosAutomaticos[parcela.id].diasAtraso} <br /></span>
                              )}
                              {jurosAutomaticos[parcela.id]?.percentual !== undefined && (
                                <span>Percentual: {jurosAutomaticos[parcela.id].percentual}%</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(valoresPagamento[parcela.id] || parcela.valor)}</TableCell>
                        <TableCell>{formatCurrency(valoresPagos[parcela.id] || 0)}</TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={novasDatasVencimento[parcela.id] || ''}
                            onChange={e => handleNovaDataVencimentoChange(parcela.id, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={!!pagarApenasJuros[parcela.id]}
                            onChange={() => handlePagarApenasJuros(parcela)}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={aplicarJurosAutomaticos}
                  onChange={e => setAplicarJurosAutomaticos(e.target.checked)}
                  color="primary"
                />
              }
              label="Juros Automáticos"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aplicarJurosEAtualizar}
                  onChange={e => setAplicarJurosEAtualizar(e.target.checked)}
                  color="primary"
                />
              }
              label="Apenas juros e renovar vencimento"
            />
          </Box>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" gutterBottom>Juros Manuais</Typography>
            <Box display="flex" alignItems="center" mb={2}>
              <ToggleButtonGroup
                exclusive
                value={tipoJuros}
                onChange={(e, newValue) => { if (newValue !== null) setTipoJuros(newValue); }}
                size="small"
                aria-label="Tipo de juros manuais"
              >
                <ToggleButton value="%" aria-label="Percentual">Percentual (%)</ToggleButton>
                <ToggleButton value="R$" aria-label="Valor Fixo">Valor Fixo (R$)</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            console.log('tipoJuros:', tipoJuros);
            {(String(tipoJuros).includes('%') || tipoJuros === 'percentual') ? (
              <TextField
                label="Juros (%)"
                type="number"
                size="small"
                value={jurosPercentual}
                onChange={(e) => setJurosPercentual(e.target.value)}
                fullWidth
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            ) : (
              <TextField
                label="Valor de Juros (R$)"
                type="number"
                size="small"
                value={valorJurosFixo}
                onChange={(e) => setValorJurosFixo(e.target.value)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
        <Button onClick={onClose} fullWidth sx={{ fontSize: { xs: '1rem', sm: '1rem' }, py: 1 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmarPagamento}
          disabled={parcelasSelecionadas.length === 0 || calcularTotais().totalPagoSelecionado <= 0 || emProcessamento}
          fullWidth
          sx={{ fontSize: { xs: '1rem', sm: '1rem' }, py: 1 }}
        >
          {emProcessamento ? 'Processando...' : 'Confirmar Pagamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalPagamento;
